/**
 * Editor Adjustments Delta
 *
 * `photo.adjustments` stores ABSOLUTE slider values: the company profile
 * baseline that Sati applied at ingest, fused with whatever the editor
 * changed on top of it. An FTP destination can opt out of the company
 * profile for a graded delivery, and that case needs the editor's CHANGES
 * ONLY, applied to the untouched original.
 *
 * The absolute values cannot be decomposed server-side, so the client -
 * which knows the baseline it loaded - computes the delta and sends it
 * alongside the absolute values as `adjustments_delta`.
 *
 * SEMANTICS: the delta is the editor's MOVEMENT, not their final value.
 * Profile sets contrast 12, editor moves it to 15 -> delta is 3.
 * "Underexposed is underexposed regardless of style" - this is editorial
 * work with strict rules, not creative retouching.
 */

import { mergeWithDefaults } from '../utils/imageAdjustments';

// Slider steps are coarse; anything smaller than this is float noise from
// unit conversions (Sati -1..1 <-> Tank -100..100), not editor movement.
const EPSILON = 1e-9;

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function deepEqual(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/**
 * Diff one level of the adjustments tree.
 *
 * - Numeric leaves subtract. A zero difference is omitted entirely.
 * - Nested objects (sharpening, color_grading) recurse; the key is dropped
 *   when every leaf below it is zero.
 * - Everything else is NOT subtractable - `tone_curve` is the case that
 *   matters: it is a list of [input, output] control points, and the
 *   difference of two curves is not a curve. Subtracting point-wise would
 *   produce a nonsense curve, and two curves can describe the same shape
 *   with a different number of points, so there is nothing to subtract
 *   against. So it passes through VERBATIM when it differs from the
 *   baseline at all, and is omitted when it is identical. A verbatim
 *   tone curve is safe for the delta consumer because the curve the editor
 *   left behind is the whole intent - there is no baseline curve to
 *   preserve once the profile is opted out of.
 */
function diffNode(current, baseline) {
  const delta = {};

  for (const key of Object.keys(current)) {
    const cur = current[key];
    const base = baseline?.[key];

    if (isPlainObject(cur)) {
      const nested = diffNode(cur, isPlainObject(base) ? base : {});
      if (Object.keys(nested).length > 0) {
        delta[key] = nested;
      }
      continue;
    }

    if (typeof cur === 'number' && typeof base === 'number') {
      const movement = cur - base;
      if (Math.abs(movement) > EPSILON) {
        delta[key] = movement;
      }
      continue;
    }

    // Not subtractable (tone_curve, or a leaf the baseline never had):
    // pass the current value through verbatim when it changed.
    if (!deepEqual(cur, base)) {
      delta[key] = cur;
    }
  }

  return delta;
}

/**
 * Compute the editor's movement relative to the loaded baseline.
 *
 * @param {Object} current - Current absolute adjustments (Tank format)
 * @param {Object} baseline - Adjustments as loaded from the DB or from
 *   sati_applied_settings, i.e. the values the editor started from
 * @returns {Object} Movement-only delta. `{}` means the editor changed
 *   nothing, which is a valid payload the backend reads as "no movement".
 */
export function computeAdjustmentsDelta(current, baseline) {
  // No baseline means we do not know what the editor started from. Sending
  // a wrong delta is worse than sending none.
  if (!current || !baseline) return {};

  return diffNode(mergeWithDefaults(current), mergeWithDefaults(baseline));
}

/**
 * Recover the baseline the editor originally started from.
 *
 * Re-opening an edited photo would otherwise take the FUSED absolute value as
 * the new baseline, so a second session's delta would describe only that
 * session and the first session's movement would be lost from the record.
 * Since `delta = absolute - baseline`, the baseline is `absolute - delta`.
 *
 * LIMIT, deliberate: a value that was passed through verbatim rather than
 * subtracted (the `tone_curve` case) cannot be reversed - the delta holds the
 * editor's curve, not a difference, so the baseline curve is simply not
 * recorded anywhere. Those keys keep the loaded value, which means a curve
 * change does not survive a reload as movement. Pretending otherwise would
 * invent a baseline.
 *
 * @param {Object} absolute - `photo.adjustments`, the stored fused values
 * @param {Object} delta - `photo.adjustments_delta`, the stored movement
 * @returns {Object} The reconstructed baseline, in Tank format.
 */
export function reconstructBaseline(absolute, delta) {
  const merged = mergeWithDefaults(absolute || {});
  if (!delta || Object.keys(delta).length === 0) return merged;

  const undo = (node, deltaNode) => {
    const out = { ...node };
    for (const key of Object.keys(deltaNode)) {
      const movement = deltaNode[key];
      const value = node?.[key];
      if (isPlainObject(movement) && isPlainObject(value)) {
        out[key] = undo(value, movement);
      } else if (typeof movement === 'number' && typeof value === 'number') {
        out[key] = value - movement;
      }
      // Non-subtractable: leave the loaded value as the baseline. See above.
    }
    return out;
  };

  return undo(merged, delta);
}
